import React, { useState, useEffect, useRef } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { FiArrowLeft, FiEdit } from 'react-icons/fi';
import { ROUTES } from 'constants/routes';
import { getNameInitials } from 'helpers';
import ImageView from 'Components/Image/ImageView';
import ContentPlanner from 'Containers/Content Planner/ContentPlanner';
import Documents from 'Containers/Documents/Documents';
import Integrations from 'Containers/Integrations/Integrations';
import StyleGuide from 'Containers/StyleGuide/StyleGuide';
import CampaignShareModal from 'Components/Modals/CampaignShareModal/CampaignShareModal';
import StyleClasses from './Projects.module.scss';
import ProjectOverview from './ProjectOverview';
import { PROJECT_TABS } from 'constants/projects';
import useProject from 'hooks/projects/useProject';
import Loader from 'Components/Loader/Loader';
import Publish from 'Containers/Editor/Publish';
import ProjectTabs from './ProjectTabs/ProjectTabs';
import ProjectDrawer from './ProjectDrawer';
import { useSelector, useDispatch } from 'react-redux';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import useUpdateProject from 'hooks/projects/useUpdateProject';
import { success as successToast, error as errorToast } from 'helpers/toaster';
import { setLoader } from 'redux/slices/loader';

const ProjectDetail = () => {
  const { projectId, projectType, folderSlug } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const isContentTabOn = !!(projectType || folderSlug);
  const dispatch = useDispatch();

  const query = new URLSearchParams(search);
  const defaultTab = query?.get('tab');

  const [, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState(
    defaultTab || PROJECT_TABS.plan,
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [projectDrawerView, setProjectDrawerView] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editableTitleValue, setEditableTitleValue] = useState('');
  const titleInputRef = useRef(null);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const { user } = useSelector((state) => state.auth);
  const { id: userId, subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};

  const { project, isLoading, refetch } = useProject({ projectId });
  const { mutateAsync: updateProject, isLoading: isUpdateLoading } = useUpdateProject();

  useEffect(() => {
    if (isContentTabOn) {
      setSelectedTab(PROJECT_TABS.content);
      setSearchParams({ tab: PROJECT_TABS.content });
    }
  }, [isContentTabOn]);

  useEffect(() => {
    if (project?.name) {
      setEditableTitleValue(project.name);
    }
  }, [project]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const { editors } = project || {};
  const editorsLength = editors?.length || 0;
  const tabs = [
    // {
    //   key: 'overview',
    //   label: 'Overview',
    //   content: <ProjectOverview project={project} fetchProject={refetch} />,
    // },
    {
      key: 'plan',
      label: 'Planner',
      content: <ContentPlanner projectView={true} noMargin={true} campaignBrandId={project?.business_id} />,
    },
    {
      key: 'content',
      label: 'Content',
      content: (
        <Documents
          projectView={false}
          projectType={projectType}
          noMargin={true}
        />
      ),
    },
    // {
    //   key: 'styleGuide',
    //   label: 'Style Guide',
    //   content: <StyleGuide projectId={projectId} />,
    // },
    // {
    //   key: 'channels',
    //   label: 'Channels',
    //   content: (
    //     <>
    //       <Integrations projectId={projectId} />
    //       {/* <Publish /> */}
    //     </>
    //   ),
    // },
  ];

  // Title editing functions
  const startEditingTitle = () => {
    if (currentSubscription?.plan_name === 'Basic Plan') {
      setShowUpgradeModal(true);
      return;
    }
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setEditableTitleValue(e.target.value);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveTitleChanges();
    } else if (e.key === 'Escape') {
      cancelTitleEdit();
    }
  };

  const cancelTitleEdit = () => {
    setEditableTitleValue(project?.name || '');
    setIsEditingTitle(false);
  };

  const saveTitleChanges = async () => {
    if (!editableTitleValue.trim()) {
      cancelTitleEdit();
      return;
    }

    if (editableTitleValue === project?.name) {
      setIsEditingTitle(false);
      return;
    }

    // Show loader
    dispatch(
      setLoader({
        isLoading: true,
        heading: 'Updating campaign...',
        description: 'Please wait...',
      })
    );

    try {
      // This is the key change - use the same format as in ProjectDrawer
      const payload = {
        projectId: projectId,
        body: {
          ...project,
          name: editableTitleValue.trim()
        }
      };

      await updateProject(payload);
      
      successToast('Campaign name updated');
      setIsEditingTitle(false);
      refetch(); // Refresh project data
    } catch (error) {
      console.error("Error updating project title:", error);
      errorToast('Failed to update campaign name');
      setEditableTitleValue(project?.name || ''); // Reset to original
      setIsEditingTitle(false);
    } finally {
      // Hide loader
      dispatch(
        setLoader({
          isLoading: false,
        })
      );
    }
  };

  // Styles for the title elements
  const titleDisplayStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };
  
  const titleTextStyle = {
    margin: '0',
    fontWeight: '500',
    fontSize: '20px',
  };
  
  const editIconStyle = {
    marginLeft: '8px',
    opacity: '0.7',
  };
  
  const titleInputStyle = {
    padding: '4px 8px',
    border: '1px solid #00A7B7',
    borderRadius: '4px',
    fontSize: '20px',
    fontWeight: '500',
    width: '100%',
    minWidth: '250px',
    outline: 'none',
  };

  return (
    <>
      {isLoading ? (
        <Loader inline loaderHeading="Fetching Campaign..." />
      ) : (
        <>
          <div className={StyleClasses.ProjectDetail_titleContainer}>
            <div className={StyleClasses.ProjectDetail_titleContainer_arrow}>
              <FiArrowLeft
                className={StyleClasses.ProjectDetail_titleContainer_arrow}
                size={24}
                color="#344054"
                onClick={() => navigate(ROUTES.Projects)}
              />
            </div>
            <div className={StyleClasses.ProjectDetail_titleContainer_title}>
              {isEditingTitle ? (
                <input
                  ref={titleInputRef}
                  type="text"
                  style={titleInputStyle}
                  value={editableTitleValue}
                  onChange={handleTitleChange}
                  onBlur={saveTitleChanges}
                  onKeyDown={handleTitleKeyDown}
                  maxLength={100}
                />
              ) : (
                <div style={titleDisplayStyle} onClick={startEditingTitle}>
                  <p style={titleTextStyle}>{project?.name}</p>
                  <FiEdit style={editIconStyle} size={16} color="#667085" />
                </div>
              )}
            </div>

            <button
              onClick={
                currentSubscription?.plan_name === 'Basic Plan'
                  ? () => setShowUpgradeModal(true)
                  : () => setProjectDrawerView(true)
              }
              className="cancel_btn text-nowrap"
            >
              Edit Campaign
            </button>
            <div
              className="d-flex stacked-Images-rounded"
              style={{ alignItems: 'center' }}
            >
              <div
                className={StyleClasses.ProjectDetail_titleContainer_editors}
                onClick={() => setShareModalOpen(true)}
              >
                {editors?.slice(0, 3)?.map(({ id, avatar, name: userName }) => {
                  return avatar ? (
                    <ImageView
                      key={id}
                      src={avatar}
                      class="rounded-avatar-bordered"
                    />
                  ) : (
                    <div
                      key={id}
                      className={`rounded-profile ${StyleClasses.avatar}`}
                    >
                      {getNameInitials(userName)}
                    </div>
                  );
                })}

                {editorsLength > 3 && (
                  <div
                    className={
                      StyleClasses.ProjectDetail_titleContainer_editorCount
                    }
                  >
                    {`+${editorsLength - 3}`}
                  </div>
                )}
              </div>

              <div
                className={StyleClasses.ProjectDetail_titleContainer_editorAdd}
                onClick={
                  currentSubscription?.plan_name === 'Basic Plan'
                    ? () => setShowUpgradeModal(true)
                    : () => setShareModalOpen(true)
                }
              >
                +
              </div>
            </div>
          </div>

          <div className={`d-flex flex-column gap-2 mt-1`}>
            <div className={StyleClasses.Projects__Views}>
              <ProjectTabs tabs={tabs} />
            </div>
          </div>
        </>
      )}

      {shareModalOpen && (
        <CampaignShareModal
          show={shareModalOpen}
          campaign={project}
          editors={editors}
          currentUser={user}
          refetch={refetch}
          onHide={() => setShareModalOpen(false)}
        />
      )}
      {projectDrawerView && (
        <ProjectDrawer
          show={projectDrawerView}
          onHide={() => {
            setProjectDrawerView(false);
          }}
          action="edit"
          project={project}
          // refreshDashboard={async () => {
          //   await fetchProject();
          // }}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
        />
      )}
    </>
  );
};

export default ProjectDetail;